import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation, Outlet } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContextType';
import Login from './components/Login/Login';
import Register from './components/Login/Register';
import ForgotPassword from './components/Login/ForgotPassword';
import ResetPassword from './components/Login/ResetPassword';
import EmailVerification from './components/EmailVerification/EmailVerification';
import ColabfoldTool from './components/ColabfoldTool/ColabfoldTool';
import RFDiffusionTool from './components/RFDiffusionTool/RFDiffusionTool';
import ESMIFTool from './components/ESMIFTool/ESMIFTool';
import JobDetail from './components/JobDetails/JobDetail';
import Layout from './components/Layout/Layout';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';
import AWSSettings from './components/Settings/AWSSettings';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ModelType } from '@/types/colabfold';
import AllJobsList from './components/AllJobsList';
import OptimizationResults from './components/OptimizationResults';
import OptimizationForm from './components/OptimizationForm';
import UsageStats from './components/Admin/UsageStats';
import UserSettings from "./components/UserSettings/UserSettings";
import AdminUserQuotas from './components/AdminUserQuotas';
import PipelineList from './components/PipelineList';
import PipelineForm from './components/PipelineForm';
import PipelineDetail from './components/PipelineDetail';

// Wrapper component to provide URL parameters to JobDetail
const JobDetailWrapper: React.FC = () => {
  const { id, modelType } = useParams<{ id: string; modelType: string }>();
  return <JobDetail jobId={id!} modelType={modelType as ModelType} />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Show loading state while authentication is being checked
  if (loading) {
    return <div>Loading...</div>;
  }

  // Routes available for both authenticated and non-authenticated users
  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/jobs" /> : <Login />} />
      <Route path="/register" element={isAuthenticated ? <Navigate to="/jobs" /> : <Register />} />
      <Route path="/verify-email" element={<EmailVerification />} />
      <Route path="/forgot-password" element={isAuthenticated ? <Navigate to="/jobs" /> : <ForgotPassword />} />
      <Route path="/reset-password" element={isAuthenticated ? <Navigate to="/jobs" /> : <ResetPassword />} />
      
      {/* Protected routes that require authentication */}
      {isAuthenticated ? (
        <Route element={<Layout><Outlet /></Layout>}>
          <Route path="/jobs" element={<AllJobsList />} />
          <Route path="/colabfold/new" element={<ColabfoldTool />} />
          <Route path="/rfdiffusion/new" element={<RFDiffusionTool />} />
          <Route path="/esmif/new" element={<ESMIFTool />} />
          <Route path="/:modelType/jobs/:id" element={<JobDetailWrapper />} />
          <Route path="/optimize/new" element={<OptimizationForm />} />
          <Route path="/optimize/results" element={<OptimizationResults />} />
          <Route path="/settings/aws" element={<AWSSettings />} />
          <Route path="/settings/user" element={<UserSettings />} />
          <Route path="/admin/stats" element={<UsageStats />} />
          <Route path="/admin/quotas" element={<AdminUserQuotas />} />
          <Route path="/pipelines" element={<PipelineList />} />
          <Route path="/pipelines/new" element={<PipelineForm />} />
          <Route path="/pipelines/:id" element={<PipelineDetail />} />
          {/* Redirect to jobs if no route matches */}
          <Route path="*" element={<Navigate to="/jobs" />} />
        </Route>
      ) : (
        // Redirect to login for all other routes if not authenticated
        <Route path="*" element={<Navigate to="/login" state={{ from: location }} />} />
      )}
    </Routes>
  );
};

const queryClient = new QueryClient();

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <AppRoutes />
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App; 